package org.gymly.util

import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import org.gymly.data.AdminRepo
import org.gymly.data.PortalRepo

data class DownloadedFile(
    val bytes: ByteArray,
    val fileName: String,
    val mimeType: String,
)

/**
 * Descarga un recurso autenticado (comprobantes de pago/venta).
 */
suspend fun downloadAuthenticatedFile(
    url: String,
    fallbackName: String = "comprobante",
): DownloadedFile {
    val client = when {
        !AdminRepo.session.accessToken.isNullOrBlank() -> AdminRepo.httpClient
        else -> PortalRepo.httpClient
    }
    val response = client.get(url)
    if (!response.status.isSuccess()) {
        throw IllegalStateException(response.bodyAsText().take(400).ifBlank { "No se pudo descargar el archivo." })
    }
    val bytes = response.body<ByteArray>()
    if (bytes.isEmpty()) throw IllegalStateException("El archivo está vacío.")
    val mime = response.headers[HttpHeaders.ContentType]
        ?.substringBefore(';')
        ?.trim()
        ?.ifBlank { null }
        ?: "application/octet-stream"
    val disposition = response.headers[HttpHeaders.ContentDisposition].orEmpty()
    val parsed = parseContentDispositionFileName(disposition, fallbackName)
    val ext = when {
        mime == "application/pdf" -> ".pdf"
        mime == "image/png" -> ".png"
        mime == "image/webp" -> ".webp"
        mime == "image/gif" -> ".gif"
        mime.startsWith("image/") -> ".jpg"
        else -> ""
    }
    val fileName = when {
        parsed.contains('.') -> parsed
        disposition.isBlank() -> "$fallbackName$ext"
        else -> parsed
    }
    return DownloadedFile(bytes = bytes, fileName = fileName, mimeType = mime)
}

fun looksLikeProofImage(contentType: String?): Boolean {
    val ct = contentType?.trim().orEmpty()
    if (ct.startsWith("image/")) return true
    // Sin content-type: muchos comprobantes son fotos; intentamos vista previa.
    if (ct.isBlank() || ct == "application/octet-stream") return true
    return false
}

fun looksLikeProofPdf(contentType: String?): Boolean =
    contentType?.trim()?.equals("application/pdf", ignoreCase = true) == true
